import {
  ApiOutlined,
  CodeOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { Card, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";

const { Title, Paragraph, Link, Text } = Typography;

interface StackItem {
  name: string;
  desc: string;
}

export default function About(): JSX.Element {
  const { t } = useTranslation();

  // Dynamische Swagger URL basierend auf Environment
  // Funktioniert mit local (/api), dev (Docker), und prod (externe URL)
  const getSwaggerUrl = (): string => {
    const apiUrl = import.meta.env.VITE_API_URL || "/api";

    // Wenn API URL relativ ist (/api)
    if (apiUrl.startsWith("/")) {
      // Local dev: Frontend auf 5173, Backend auf 8000
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
        return `http://${window.location.hostname}:8000/docs`;
      }
      // Production: Frontend und API auf gleiche Domain
      return `${window.location.origin}/docs`;
    }

    // Wenn absolut URL (Dev Docker/Prod): entferne /api Suffix und hänge /docs an
    // Dev: http://backend:8000/api → http://backend:8000/docs
    // Prod: https://api.example.com/api → https://api.example.com/docs
    const baseUrl = apiUrl.replace(/\/api\/?$/, "");
    return `${baseUrl}/docs`;
  };

  const frontendStack = t("about.frontendStack", { returnObjects: true }) as StackItem[];
  const backendStack = t("about.backendStack", { returnObjects: true }) as StackItem[];
  const security = t("about.security", { returnObjects: true }) as StackItem[];
  const features = t("about.features", { returnObjects: true }) as string[];
  const localPoints = t("about.local.points", { returnObjects: true }) as string[];
  const devPoints = t("about.dev.points", { returnObjects: true }) as string[];
  const prodPoints = t("about.prod.points", { returnObjects: true }) as string[];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: 800 }}>
      <Card>
        <Title level={2}>
          <RocketOutlined /> {t("about.title")}
        </Title>
        <Paragraph>
          {t("about.introLine1")}
          <br />
          {t("about.introLine2")}
          <br />
          {t("about.introLine3")}
        </Paragraph>
      </Card>

      <Card
        title={
          <>
            <CodeOutlined /> {t("about.frontendStackTitle")}
          </>
        }
      >
        <ul>
          {frontendStack.map((item) => (
            <li key={item.name}>
              <strong>{item.name}</strong> - {item.desc}
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title={
          <>
            <ApiOutlined /> {t("about.backendStackTitle")}
          </>
        }
      >
        <ul>
          {backendStack.map((item) => (
            <li key={item.name}>
              <strong>{item.name}</strong> - {item.desc}
            </li>
          ))}
        </ul>
      </Card>

      <Card title={t("about.environmentsTitle")}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Tag color="blue">local</Tag>
            <Text strong> {t("about.local.title")}</Text>
            <Paragraph style={{ marginTop: 8 }}>
              {localPoints.map((point, idx) => (
                <span key={point}>
                  {idx > 0 && <br />}• {point}
                </span>
              ))}
            </Paragraph>
          </div>

          <div>
            <Tag color="orange">dev</Tag>
            <Text strong> {t("about.dev.title")}</Text>
            <Paragraph style={{ marginTop: 8 }}>
              {devPoints.map((point, idx) => (
                <span key={point}>
                  {idx > 0 && <br />}• {point}
                </span>
              ))}
            </Paragraph>
          </div>

          <div>
            <Tag color="green">main</Tag>
            <Text strong> {t("about.prod.title")}</Text>
            <Paragraph style={{ marginTop: 8 }}>
              {prodPoints.map((point, idx) => (
                <span key={point}>
                  {idx > 0 && <br />}• {point}
                </span>
              ))}
            </Paragraph>
          </div>
        </Space>
      </Card>

      <Card title={t("about.apiDocsTitle")}>
        <Paragraph>{t("about.apiDocsIntro")}</Paragraph>
        <Paragraph>
          <Link href={getSwaggerUrl()} target="_blank" strong>
            {getSwaggerUrl()} →
          </Link>
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: "12px" }}>
          {t("about.apiDocsFooter")}
        </Paragraph>
      </Card>

      <Card title={t("about.featuresTitle")}>
        <ul>
          {features.map((feature) => (
            <li key={feature}>✅ {feature}</li>
          ))}
        </ul>
      </Card>

      <Card
        title={
          <>
            <SafetyCertificateOutlined /> {t("about.securityTitle")}
          </>
        }
      >
        <ul>
          {security.map((item) => (
            <li key={item.name}>
              <strong>{item.name}</strong> - {item.desc}
            </li>
          ))}
        </ul>
        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          {t("about.securityFooter")}
        </Paragraph>
      </Card>

      <Card title={t("about.environmentInfoTitle")}>
        <Space direction="vertical">
          <Text>
            <strong>{t("about.apiUrlLabel")}</strong> {import.meta.env.VITE_API_URL || "/api"}
          </Text>
          <Text>
            <strong>{t("about.modeLabel")}</strong> {import.meta.env.MODE}
          </Text>
          <Text>
            <strong>{t("about.baseUrlLabel")}</strong> {window.location.origin}
          </Text>
        </Space>
      </Card>
    </Space>
  );
}
